/**
 * Left navigation. Brand has moved to the top bar; this view starts with
 * the search box, then Favourites + All Tables accordions, and ends with
 * a ChatGPT-style user chip + popup (avatar/name/role; click to reveal
 * Sign out).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  IconChevronRight,
  IconDots,
  IconLogout,
  IconSettings,
  IconReload,
  IconSchema,
  IconSearch,
  IconStar,
  IconStarFilled,
  IconTable,
  IconX,
} from "@tabler/icons-react";

const logoUrl = "/kse-logo.png";
import { Menu, Popover, Tooltip, UnstyledButton } from "@mantine/core";
import { SidebarSkeleton } from "./SidebarSkeleton";
import { useMsal } from "@azure/msal-react";
import type { TableSummary } from "../api/types";
import { useFavorites } from "../lib/favorites";
import { qk } from "../api/keys";

type Props = {
  tables: TableSummary[] | undefined;
  loading?: boolean;
  isAdmin?: boolean;
  user?: { name?: string; preferred_username?: string; roles: string[] };
  /** Open the Settings modal (self-contained — no URL change). */
  onOpenSettings?: () => void;
};

function deriveRole(
  tables: TableSummary[] | undefined,
  isAdmin: boolean,
): "Admin" | "Editor" | "Viewer" {
  if (isAdmin) return "Admin";
  if (!tables) return "Viewer";
  return tables.some(
    (t) => t.permissions.insert || t.permissions.update || t.permissions.delete,
  )
    ? "Editor"
    : "Viewer";
}

function avatarColor(seed: string) {
  let h = 0;
  for (const c of seed) h = (h * 31 + c.charCodeAt(0)) % 360;
  return `oklch(0.7 0.13 ${h})`;
}

function initials(input: string | undefined) {
  if (!input) return "?";
  const at = input.indexOf("@");
  const name = at > 0 ? input.slice(0, at) : input;
  const parts = name.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export function Sidebar({ tables, loading, isAdmin = false, onOpenSettings }: Props) {
  const navigate = useNavigate();
  const { schema: activeSchema, name: activeName } = useParams();
  const activeKey =
    activeSchema && activeName ? `${activeSchema}.${activeName}` : null;

  const [search, setSearch] = useState("");
  const [openSchemas, setOpenSchemas] = useState<Record<string, boolean>>({});
  const [favOpen, setFavOpen] = useState(true);
  const [allOpen, setAllOpen] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);

  const qc = useQueryClient();
  const { isFav, toggle } = useFavorites();
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const grouped = useMemo(() => {
    const map = new Map<string, TableSummary[]>();
    for (const t of tables ?? []) {
      if (!map.has(t.schema)) map.set(t.schema, []);
      map.get(t.schema)!.push(t);
    }
    for (const arr of map.values()) arr.sort((a, b) => a.name.localeCompare(b.name));
    return [...map.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, list]) => ({ name, tables: list }));
  }, [tables]);

  const favoriteEntries = useMemo(() => {
    const out: TableSummary[] = [];
    for (const t of tables ?? []) {
      if (isFav(`${t.schema}.${t.name}`)) out.push(t);
    }
    out.sort((a, b) => `${a.schema}.${a.name}`.localeCompare(`${b.schema}.${b.name}`));
    return out;
  }, [tables, isFav]);

  useEffect(() => {
    if (grouped.length === 0) return;
    setOpenSchemas((prev) => {
      if (Object.keys(prev).length > 0) {
        if (activeSchema && prev[activeSchema] === undefined) {
          return { ...prev, [activeSchema]: true };
        }
        return prev;
      }
      const next: Record<string, boolean> = {};
      grouped.forEach((s, i) => {
        next[s.name] = i === 0;
      });
      if (activeSchema) next[activeSchema] = true;
      return next;
    });
  }, [grouped, activeSchema]);

  const q = search.trim().toLowerCase();
  const matches = (t: TableSummary) =>
    t.name.toLowerCase().includes(q) || t.schema.toLowerCase().includes(q);

  const filteredGroups = useMemo(() => {
    if (!q) return grouped;
    return grouped
      .map((s) => ({ ...s, tables: s.tables.filter(matches) }))
      .filter((s) => s.tables.length > 0);
  }, [grouped, q]);

  const filteredFavs = q ? favoriteEntries.filter(matches) : favoriteEntries;

  useEffect(() => {
    if (!q) return;
    setOpenSchemas((prev) => {
      const next = { ...prev };
      filteredGroups.forEach((s) => {
        next[s.name] = true;
      });
      return next;
    });
  }, [q, filteredGroups]);

  const role = deriveRole(tables, isAdmin);

  return (
    <aside className="kse-sidebar">
      {/* Search at top */}
      <div className="kse-sidebar__search">
        <div className="kse-search-input">
          <IconSearch size={13} color="var(--fg-faint)" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search tables…"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                // First Escape clears any typed value; second blurs.
                if (search) {
                  setSearch("");
                } else {
                  e.currentTarget.blur();
                }
              }
            }}
          />
          {search ? (
            <button
              type="button"
              className="kse-icon-btn"
              style={{ width: 18, height: 18 }}
              onClick={() => setSearch("")}
              aria-label="Clear search"
            >
              <IconX size={11} />
            </button>
          ) : (
            <span className="kse-kbd">⌘K</span>
          )}
        </div>
      </div>

      {/* Brand below search; no border under it */}
      <div className="kse-sidebar__brand">
        <div className="kse-sidebar__brand-mark">
          <img src={logoUrl} alt="KSE" />
        </div>
        <div className="kse-sidebar__brand-text">
          {/* "Metadata" SemiBold (600), "Manager" Regular (400) */}
          <span className="kse-brand-wordmark">
            <span className="kse-brand-wordmark__strong">Metadata</span>{" "}
            <span className="kse-brand-wordmark__light">Manager</span>
          </span>
        </div>
      </div>

      <div className="kse-sidebar__scroll">
        {loading && <SidebarSkeleton />}

        {/* Favourites */}
        {!loading && favoriteEntries.length > 0 && (
          <div className="kse-accordion-section">
            <button
              type="button"
              className="kse-accordion-header kse-accordion-header--top"
              data-open={favOpen}
              onClick={() => setFavOpen((o) => !o)}
            >
              <span className="kse-accordion-header__chevron">
                <IconChevronRight size={11} />
              </span>
              <IconStarFilled size={12} color="var(--warn)" />
              <span className="kse-accordion-header__name">Favourites</span>
              <span className="kse-accordion-header__count">
                {filteredFavs.length}
              </span>
            </button>

            {favOpen && (
              <>
                {filteredFavs.length === 0 && q && (
                  <div className="kse-empty-mini">No matches</div>
                )}
                {filteredFavs.map((t) => (
                  <TableItem
                    key={`fav.${t.schema}.${t.name}`}
                    table={t}
                    activeKey={activeKey}
                    onPick={() => navigate(`/tables/${t.schema}/${t.name}`)}
                    isFavorite
                    showSchemaPrefix
                    onToggleFav={() => toggle(`${t.schema}.${t.name}`)}
                  />
                ))}
              </>
            )}
          </div>
        )}

        {/* All Tables */}
        {!loading && (
          <div className="kse-accordion-section">
            <div
              className="kse-accordion-header kse-accordion-header--top"
              data-open={allOpen}
              role="button"
              tabIndex={0}
              onClick={() => setAllOpen((o) => !o)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setAllOpen((o) => !o);
                }
              }}
            >
              <span className="kse-accordion-header__chevron">
                <IconChevronRight size={11} />
              </span>
              <IconTable size={13} color="var(--fg-faint)" />
              <span className="kse-accordion-header__name">All Tables</span>
              <span className="kse-accordion-header__trailing">
                <Tooltip label="Refresh schema">
                  <button
                    type="button"
                    className="kse-icon-btn kse-accordion-header__action"
                    onClick={async (e) => {
                      e.stopPropagation();
                      await fetch("/api/schema/refresh", {
                        method: "POST",
                        headers: { "content-type": "application/json" },
                      }).catch(() => {});
                      qc.invalidateQueries({ queryKey: qk.tables });
                    }}
                    aria-label="Refresh"
                  >
                    <IconReload size={13} />
                  </button>
                </Tooltip>
                <span className="kse-accordion-header__count">
                  {filteredGroups.reduce((n, s) => n + s.tables.length, 0)}
                </span>
              </span>
            </div>

            {allOpen &&
              filteredGroups.map((s) => {
                const open = !!openSchemas[s.name];
                return (
                  <div key={s.name} className="kse-accordion-section kse-accordion-section--nested">
                    <button
                      type="button"
                      className="kse-accordion-header kse-accordion-header--sub"
                      data-open={open}
                      onClick={() =>
                        setOpenSchemas((o) => ({ ...o, [s.name]: !o[s.name] }))
                      }
                    >
                      <span className="kse-accordion-header__chevron">
                        <IconChevronRight size={11} />
                      </span>
                      <IconSchema size={12} color="var(--fg-faint)" />
                      <span className="kse-accordion-header__name">{s.name}</span>
                      <span className="kse-accordion-header__count">
                        {s.tables.length}
                      </span>
                    </button>

                    {open &&
                      s.tables.map((t) => (
                        <TableItem
                          key={`all.${t.schema}.${t.name}`}
                          table={t}
                          activeKey={activeKey}
                          onPick={() =>
                            navigate(`/tables/${t.schema}/${t.name}`)
                          }
                          isFavorite={isFav(`${t.schema}.${t.name}`)}
                          onToggleFav={() => toggle(`${t.schema}.${t.name}`)}
                        />
                      ))}
                  </div>
                );
              })}

            {filteredGroups.length === 0 && q && (
              <div className="kse-empty-mini" style={{ paddingLeft: 16 }}>
                No tables match “{search}”
              </div>
            )}
          </div>
        )}
      </div>

      {/* User chip pinned to the bottom of the sidebar, separated from
          the scrollable list by a horizontal rule (ChatGPT-style). */}
      <div className="kse-sidebar__user">
      <Popover
        position="top-start"
        offset={6}
        width="target"
        shadow="xl"
        radius="md"
        withinPortal
      >
        <Popover.Target>
          <UnstyledButton
            className="kse-userchip"
            aria-label="Account menu"
          >
            <div
              className="kse-userchip__avatar"
              style={{ background: avatarColor(account?.username ?? "") }}
            >
              {initials(account?.username)}
            </div>
            <div className="kse-userchip__meta">
              <div className="kse-userchip__name">
                {account?.name ?? account?.username ?? "Signed in"}
              </div>
              <div className="kse-userchip__role">{role}</div>
            </div>
          </UnstyledButton>
        </Popover.Target>
        <Popover.Dropdown p={4}>
          {/* Top mirror of the chip itself — a visual anchor like ChatGPT */}
          <div className="kse-menu-item kse-menu-item--profile">
            <div
              className="kse-userchip__avatar"
              style={{ background: avatarColor(account?.username ?? "") }}
            >
              {initials(account?.username)}
            </div>
            <div className="kse-userchip__meta">
              <div className="kse-userchip__name">
                {account?.name ?? account?.username ?? "Signed in"}
              </div>
              <div className="kse-userchip__role">{role}</div>
            </div>
          </div>

          <div className="kse-menu-sep" />

          {isAdmin && (
            <button
              type="button"
              className="kse-menu-item"
              onClick={() => onOpenSettings?.()}
            >
              <IconSettings size={15} />
              <span>Settings</span>
            </button>
          )}

          <button
            type="button"
            className="kse-menu-item"
            onClick={() => instance.logoutRedirect()}
          >
            <IconLogout size={15} />
            <span>Sign out</span>
          </button>
        </Popover.Dropdown>
      </Popover>
      </div>
    </aside>
  );
}

function TableItem({
  table,
  activeKey,
  onPick,
  isFavorite,
  showSchemaPrefix,
  onToggleFav,
}: {
  table: TableSummary;
  activeKey: string | null;
  onPick: () => void;
  isFavorite: boolean;
  showSchemaPrefix?: boolean;
  onToggleFav: () => void;
}) {
  const key = `${table.schema}.${table.name}`;
  const active = key === activeKey;
  const isView = table.type === "VIEW";

  return (
    <div
      className="kse-table-item"
      data-active={active}
      onClick={onPick}
      role="button"
      tabIndex={0}
      title={key}
    >
      <span className="kse-table-item__name">
        {showSchemaPrefix && (
          <span style={{ color: "var(--fg-faint)" }}>{table.schema}.</span>
        )}
        {table.name}
      </span>
      {isView && (
        <span className="kse-table-item__viewbadge" title="View (read-only)">VIEW</span>
      )}
      <Menu position="bottom-end" withinPortal width={180} shadow="md">
        <Menu.Target>
          <button
            type="button"
            className="kse-table-item__more"
            onClick={(e) => e.stopPropagation()}
            aria-label="Table actions"
          >
            <IconDots size={14} />
          </button>
        </Menu.Target>
        <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
          <Menu.Item
            leftSection={isFavorite ? <IconStarFilled size={13} color="var(--warn)" /> : <IconStar size={13} />}
            onClick={onToggleFav}
          >
            {isFavorite ? "Remove from favourites" : "Add to favourites"}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </div>
  );
}
